import { useState } from 'react';
import { Stack, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { ACCOUNT_TYPES } from '../common/constants';
import { isVerticalIpadMini } from '../common/responsiveUtils';
import { enqueueSnackbar } from 'notistack';
import { MESSAGES } from '../common/messages';
import { validateEmployeeNumber, validateName, validateClassName, validatePassword } from '../common/validationUtils';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

const AccountEditDialog = (props) => {
  const { typography, palette } = useTheme();
  const styles = {
    cancelButton: {
      ...typography.label14,
      width: '224px',
      height: '44px',
      borderRadius: '100px',
      marginLeft: 'auto',
      marginRight: '8px',
      borderColor: palette.primary.light,
      color: palette.primary.light,
    },
    submitButton: {
      ...typography.label14bold,
      width: '232px',
      height: '44px',
      borderRadius: '100px',
      marginRight: 'auto',
      backgroundColor: palette.primary.light,
    },
    text: {
      ...typography.label14bold,
      height: '20px',
      marginBottom: '2px',
    },
  };

  const [showPassword, setShowPassword] = useState(false);
  const { account } = props;

  const handleClose = () => {
    setEmployeeNumberError('');
    setClassNameError('');
    setNameError('');
    setPasswordError('');
    props.setOpen(false);
  };

  const isDisable = () => {
    return validateEmployeeNumber(account.employeeNumber) !== '' ||
      validateName(account.name) !== '' ||
      validateClassName(account.className) !== '' ||
      validatePassword(account.password, props.isUpdate) !== '';
  };

  const handleSubmit = async () => {
    try {
      const submitData = {
        employeeNumber: account.employeeNumber,
        className: account.classNamePrefix + account.className,
        name: account.name,
        accountType: Number(account.accountType),
        password: account.password,
      };
      if (account.className === undefined) submitData.className = '';
      await axios.post('/v1/users', [submitData]);
      handleClose();
      enqueueSnackbar(props.isUpdate ? MESSAGES.ACCOUNT_UPDATED : MESSAGES.ACCOUNT_ADDED);
    } catch (err) { }
  };

  const [employeeNumberError, setEmployeeNumberError] = useState('');
  const [nameError, setNameError] = useState('');
  const [classNameError, setClassNameError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleEmployeeNumberChange = (e) => {
    const value = e.target.value;
    const copied = structuredClone(account);
    copied.employeeNumber = value;
    props.setAccount(copied);

    const errorMessage = validateEmployeeNumber(value);
    setEmployeeNumberError(errorMessage);
  };

  const handleNameChange = (e) => {
    const copied = structuredClone(account);
    copied.name = e.target.value;
    props.setAccount(copied);

    const errorMessage = validateName(e.target.value);
    setNameError(errorMessage);
  };
  const handleClassNameChange = (e) => {
    const copied = structuredClone(account);
    copied.className = e.target.value;
    props.setAccount(copied);

    const errorMessage = validateClassName(e.target.value);
    setClassNameError(errorMessage);
  };
  const handlePasswordChange = (e) => {
    const copied = structuredClone(account);
    copied.password = e.target.value;
    props.setAccount(copied);

    const errorMessage = validatePassword(e.target.value);
    setPasswordError(errorMessage);
  };

  const addUpdateText = props.isUpdate ? '更新' : '追加登録';

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      fullWidth
      maxWidth='lg'
      PaperProps={{
        sx: {
          margin: '0px',
          width: isVerticalIpadMini() ? '740px' : '980px',
          maxHeight: '90vh',
        },
      }}
      sx={{
        width: '720px',
        height: 'auto',
        maxHeight: '90vh',
        margin: 'auto',
        overflow: 'hidden',
      }}
    >

      <DialogTitle variant='body16bold' textAlign='center' position='relative' sx={{ height: '44px' }}>
        トレーニングアカウント{addUpdateText}
        <IconButton
          sx={{ position: 'absolute', right: '6px', top: '0px', bottom: '0px' }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ paddingTop: '24px' }}>
        <Stack sx={{ marginBottom: '16px' }} justifyContent="center" alignItems="center">
          <Stack sx={{ marginBottom: '16px' }}>
            <p style={{ ...styles.text, padding: '0px', height: '0px' }}>利用する運輸区（ログインした乗務員区）*</p>
            <p style={{ ...typography.label16, height: '20px' }}>{props.loginUser.organizationName}</p>
            <Stack>
              <p style={styles.text}>アカウント権限*</p>
              <RadioGroup row sx={{ alignItems: 'center' }}
                onChange={(e) => {
                  const copied = structuredClone(account);
                  copied.accountType = e.target.value;
                  props.setAccount(copied);
                }}
                value={account.accountType}
              >
                <FormControlLabel
                  control={<Radio />}
                  label={ACCOUNT_TYPES.DRIVER.label}
                  value={ACCOUNT_TYPES.DRIVER.value}
                  sx={{ marginRight: '24px' }}
                />
                <FormControlLabel
                  control={<Radio />}
                  label={ACCOUNT_TYPES.TRAINER.label}
                  value={ACCOUNT_TYPES.TRAINER.value}
                  sx={{ marginRight: '12px' }}
                />
              </RadioGroup>
            </Stack>
            <p style={styles.text}>社員番号（ログインアカウント）*</p>
            <TextField
              disabled={props.isUpdate}
              InputProps={{
                style: {
                  height: '44px',
                  width: '280px',
                  backgroundColor: 'white',
                },
              }}
              onChange={handleEmployeeNumberChange}
              value={account.employeeNumber}
              error={!!employeeNumberError}
              helperText={employeeNumberError}
            />
            <p style={styles.text}>氏名*</p>
            <TextField
              InputProps={{
                style: {
                  height: '44px',
                  width: '280px',
                  backgroundColor: 'white',
                },
              }}
              onChange={handleNameChange}
              value={account.name}
              error={!!nameError}
              helperText={nameError}
            />
            <p style={styles.text}>EC</p>
            <Stack direction='row'>
              <FormControl fullWidth sx={{ width: '77px', marginRight: '5px' }}>
                <Select
                  id="demo-simple-select"
                  value={account.classNamePrefix}
                  onChange={(e) => {
                    const copied = structuredClone(account);
                    copied.classNamePrefix = e.target.value;
                    props.setAccount(copied);
                  }}
                  sx={{ width: '77px', height: '44px' }}
                >
                  <MenuItem value={''}>&nbsp;</MenuItem>
                  <MenuItem value={'新'}>新</MenuItem>
                  <MenuItem value={'旧'}>旧</MenuItem>
                </Select>
              </FormControl>
              <TextField
                InputProps={{
                  style: {
                    height: '44px',
                    width: '48px',
                    backgroundColor: 'white',
                  },
                }}
                onChange={handleClassNameChange}
                value={account.className}
                sx={{ marginRight: '5px' }}
                error={!!classNameError}
                helperText={classNameError}
              />
              <p style={{
                ...typography.label16,
                paddingBottom: '0px',
                display: 'flex',
                alignItems: 'center',
                height: '10px',
              }}>回生</p>
            </Stack>
            <p style={styles.text}>パスワード*</p>
            <TextField
              type={showPassword ? 'text' : 'password'}
              placeholder={props.isUpdate ? '変更しない場合は空欄' : ''}
              InputProps={{
                style: {
                  height: '44px',
                  width: '280px',
                  backgroundColor: 'white',
                },
                endAdornment: (
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                ),
              }}
              onChange={handlePasswordChange}
              error={!!passwordError}
              helperText={passwordError}
            />
          </Stack>
        </Stack>
        <DialogActions sx={{ height: '44px', display: 'block', margin: '0px' }}>
          <Stack direction='row'>
            <Button
              variant='outlined' sx={styles.cancelButton}
              onClick={handleClose}
            >
              キャンセル
            </Button>
            <Button
              variant='contained' disableElevation sx={styles.submitButton}
              disabled={isDisable()}
              onClick={handleSubmit}
            >
              {addUpdateText}
            </Button>
          </Stack>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default AccountEditDialog;

AccountEditDialog.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  setContinue: PropTypes.func,
  message: PropTypes.string,
  account: PropTypes.object,
  setAccount: PropTypes.func,
  isUpdate: PropTypes.bool,
  loginUser: PropTypes.object,
};
